import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { DetailResponse, ReportArea } from 'modules/arena/reports/model/types';
import { ReportsSource } from 'modules/arena/reports/source/types';
import { Skeleton } from 'shared/components/ui/Skeleton';
import { EASE_OUT } from 'shared/components/motion/tokens';
import { cn } from 'shared/utils/cn';

interface DetailDrawerProps {
  source: ReportsSource;
  area: ReportArea;
  rowId: string | null;
  onClose: () => void;
}

const TONE = { danger: 'text-danger', success: 'text-success', muted: 'text-ink-secondary' };

/** Painel lateral com o detalhamento de uma linha (ex.: composição de um pedido). */
export function DetailDrawer({ source, area, rowId, onClose }: DetailDrawerProps) {
  const [detail, setDetail] = useState<DetailResponse | null | 'loading' | 'error'>('loading');
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!rowId) return;
    setDetail('loading');
    const controller = new AbortController();
    source
      .fetchDetail(area, rowId, controller.signal)
      .then((result) => setDetail(result ?? 'error'))
      .catch((error: Error) => error.name !== 'AbortError' && setDetail('error'));
    return () => controller.abort();
  }, [area, rowId, source]);

  useEffect(() => {
    if (!rowId) return;
    panelRef.current?.focus();
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rowId, onClose]);

  return (
    <AnimatePresence>
      {rowId && (
        <motion.div className="fixed inset-0 z-50 flex justify-end bg-ink/30" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Detalhes do registro"
            className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-surface shadow-card-hover focus:outline-none"
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 40, opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sticky top-0 flex items-start justify-between gap-3 border-b border-border/60 bg-surface px-6 py-4">
              <div>
                <h2 className="font-heading text-lg font-bold text-ink">
                  {typeof detail === 'object' && detail ? detail.title : 'Detalhes'}
                </h2>
                {typeof detail === 'object' && detail?.subtitle && <p className="text-sm text-ink-secondary">{detail.subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Fechar detalhes" className="rounded-md p-1.5 text-ink-secondary hover:bg-chip hover:text-ink">
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div className="flex flex-col gap-6 px-6 py-5">
              {detail === 'loading' && <Skeleton className="h-64 w-full" />}
              {detail === 'error' && <p className="text-sm text-danger">Não foi possível carregar os detalhes deste registro.</p>}
              {typeof detail === 'object' &&
                detail?.sections.map((section) => (
                  <section key={section.title}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">{section.title}</h3>
                    <dl className="mt-2 divide-y divide-border/50">
                      {section.rows.map((row) => (
                        <div key={row.label} className="py-2">
                          <div className="flex items-start justify-between gap-4 text-sm">
                            <dt className="text-ink">{row.label}</dt>
                            <dd className={cn('text-right font-medium tabular-nums', row.tone ? TONE[row.tone] : 'text-ink')}>{row.value}</dd>
                          </div>
                          {row.note && <p className="mt-0.5 text-xs text-ink-secondary">{row.note}</p>}
                        </div>
                      ))}
                    </dl>
                  </section>
                ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
